/**
 * Where the tour card goes relative to the element it is describing.
 *
 * Split out of TourOverlay because it is the part with actual decisions in it,
 * and because the component test environment cannot currently resolve
 * react-router-dom — pure geometry should not be untestable for that reason.
 *
 * The rules, in order:
 *   - Nothing to point at → centre it. This is also the degradation path when
 *     an anchor selector matches no element, so a renamed nav item turns the
 *     tour into a slideshow rather than into a card pinned to the top-left
 *     corner of the screen.
 *   - Room to the right → sit beside the element, which keeps it visible.
 *   - Otherwise → sit below it, clamped so the card cannot leave the viewport.
 */

export const CARD_WIDTH = 380;
export const GAP = 14;
/** Enough of the card to be readable before it is scrolled to. */
export const MIN_CARD_HEIGHT = 260;

export function cardPosition(rect, viewport = {}) {
  const vw = viewport.width ?? (typeof window !== "undefined" ? window.innerWidth : 1024);
  const vh = viewport.height ?? (typeof window !== "undefined" ? window.innerHeight : 768);

  if (!rect) return { centred: true };

  const clampTop = (v) => Math.min(Math.max(GAP, v), Math.max(GAP, vh - MIN_CARD_HEIGHT));
  const clampLeft = (v) => Math.min(Math.max(GAP, v), Math.max(GAP, vw - CARD_WIDTH - GAP));

  const roomRight = vw - (rect.left + rect.width) > CARD_WIDTH + GAP * 2;
  if (roomRight) {
    return {
      centred: false,
      left: rect.left + rect.width + GAP,
      top: clampTop(rect.top),
    };
  }

  return {
    centred: false,
    left: clampLeft(rect.left),
    top: clampTop(rect.top + rect.height + GAP),
  };
}
